import "./game.css";
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from 'react-bootstrap';
import Rack from "../Rack";
import Board from "../Board";
import Tile from "../Tile";
import ScoreBoard from "../ScoreBoard";
import Summary from "../Summary";
import Chat from "../Chat";
import Actions from "../Actions";
import { useParams, Link } from "react-router-dom";
import { useAtomValue } from 'jotai';
import { devModeAtom } from '../../atoms';
import FloatingTile from "../FloatingTile/";
import {
    calcBoardConfig as calcBoardConfigFn,
    calcRackScale as calcRackScaleFn,
} from "./layoutUtils";
import {
    getBoardBoundingBox as getBoardBoundingBoxFn,
    countPlayedTiles as countPlayedTilesFn,
    clickBoardTile as clickBoardTileFn,
    clearTilesFromBoard as clearTilesFromBoardFn,
} from "./boardUtils";
import {
    getScore as getScoreFn,
    playWord as playWordFn,
    chooseForBlank as chooseForBlankFn,
} from "./gameActions";
import {
    mouseDown as mouseDownFn,
    mouseUp as mouseUpFn,
    mouseClickOnRack as mouseClickOnRackFn,
    moving as movingFn,
    addTileToRack as addTileToRackFn,
} from "./rackUtils";
import {
    addFloatingTile as addFloatingTileFn,
    removeFloatingTile as removeFloatingTileFn,
    floatingTileClickHandler as floatingTileClickHandlerFn,
} from "./tileUtils";
import ExchangeModal from "../ExchangeModal";
import ChallengeModal from "../ChallengeModal";

function Game({connection, getGame, game, setGame, turn, connectionStatus = "connected"}) {
    const { gameId } = useParams();

    // Always trigger getGame if needed (on mount or wrong game)
    React.useEffect(() => {
        if (!game || game.id !== parseInt(gameId, 10)) {
            getGame(gameId);
        }
    }, [gameId]);

    const [showInvalidWord, setShowInvalidWord] = useState(false);
    const [showOutOfTurn, setShowOutOfTurn] = useState(false);
    const [backendError, setBackendError] = useState(null);
    const showBackendErrorsRef = useRef(
        localStorage.getItem('crabbles_showBackendErrors') !== 'false'
    );
    const [showBackendErrors, setShowBackendErrors] = useState(showBackendErrorsRef.current);
    const toggleBackendErrors = () => {
        const next = !showBackendErrors;
        setShowBackendErrors(next);
        showBackendErrorsRef.current = next;
        localStorage.setItem('crabbles_showBackendErrors', next);
    };

    const [boardScale, setBoardScale] = useState(50);
    const [boardConfig, setBoardConfig] = useState("mobile"); // mobile, tall, tv, hd
    const [sidebarWidth, setSidebarWidth] = useState(0);
    const [tooShort, setTooShort] = useState(false);
    const [tab, setTab] = useState("game");
    const [rackScale, setRackScale] = useState(75);
    const [rackWidth, setRackWidth] = useState(700);
    const [rackTiles, setRackTiles] = useState([]);
    // rack
    const [grabbedTile, setGrabbedTile] = useState(null);
    const [lastClientX, setLastClientX] = useState(null);
    const [mouseDownClientX, setMouseDownClientX] = useState(null);
    const [clientX, setClientX] = useState(0);
    const [clientY, setClientY] = useState(0);
    const [floatingTile, setFloatingTile] = useState(null);
    const [tileMoved, setTileMoved] = useState(false);
    const [pickedUpTile, setPickedUpTile] = useState(null);
    const [playedTileCount, setPlayedTileCount] = useState(0);
    const [showBlankSelector, setShowBlankSelector] = useState(false);
    const [spaceWithBlank, setSpaceWithBlank] = useState(null);
    const [debug, setDebug] = useState({});
    const [sized, setSized] = useState(false);
    const [overBoard, setOverBoard] = useState(false);
    const [boardBoundingBox, setBoardBoundingBox] = useState({});
    const devMode = useAtomValue(devModeAtom);
    const [bagTiles, setBagTiles] = useState([]);
    const [rackBoundingBox, setRackBoundingBox] = useState({});
    const [showExchangeModal, setShowExchangeModal] = useState(false);
    const [showChallengeModal, setShowChallengeModal] = useState(false);
    const [challengeResult, setChallengeResult] = useState(null);
    // US4: shown when an action is attempted while the connection is not ready
    const [showConnectionLost, setShowConnectionLost] = useState(false);

    // End-game state (T022/T023)
    const [pendingPlayOut, setPendingPlayOut] = useState(false);
    const [playOutChallengeWords, setPlayOutChallengeWords] = useState([]);
    const [playOutPlayerName, setPlayOutPlayerName] = useState("");
    const [isFinished, setIsFinished] = useState(false);
    const [showGameOverModal, setShowGameOverModal] = useState(false);
    const [finishReason, setFinishReason] = useState(null);
    const [finalScores, setFinalScores] = useState([]);
    const [winners, setWinners] = useState([]);

    // --- Rack auto-save (US1/US2): explicit dirty marking ---
    // Two distinct paths call setRackTiles:
    //   • [game] effect: server-pushed rack reset — clears dirty, resets timer
    //   • User interactions (moving, mouseClickOnRack, addTileToRack, etc.) — marks dirty
    // setRackTilesUser is passed to user-driven utilities; setRackTiles to the server path.
    // Avoids effect-based detection, which was defeated by moving() mutating tile objects
    // in-place (rackTiles and game.rack share the same JS objects), causing [game] to
    // re-snapshot already-mutated positions and make the comparison always match.
    const rackDirtyRef = useRef(false);
    const saveTimerRef = useRef(null);
    const rackTilesRef = useRef([]);
    // Refs needed so the unmount cleanup closure reads current values
    const connectionRef = useRef(connection);
    useEffect(() => { connectionRef.current = connection; }, [connection]);
    const gameMetaRef = useRef({ id: null, playerIndex: null });

    const setRackTilesUser = (tiles) => {
        rackTilesRef.current = tiles;
        setRackTiles(tiles);
        rackDirtyRef.current = true;
        const { id: savedGameId, playerIndex: savedPlayerIndex } = gameMetaRef.current;
        clearTimeout(saveTimerRef.current);
        console.log('[RackAutoSave] Timer started (5s)');
        saveTimerRef.current = setTimeout(() => {
            if (!rackDirtyRef.current) {
                console.log('[RackAutoSave] Timer expired, but rack not dirty. No save.');
                return;
            }
            rackDirtyRef.current = false;
            console.log('[RackAutoSave] Timer expired, sending SaveRack', {
                gameId: savedGameId,
                playerIndex: savedPlayerIndex,
                rack: rackTilesRef.current
            });
            connectionRef.current?.invoke('SaveRack', savedGameId, savedPlayerIndex, rackTilesRef.current)
                .then(() => {
                    console.log('[RackAutoSave] SaveRack message sent successfully');
                })
                .catch((err) => {
                    console.log('[RackAutoSave] SaveRack message failed', err);
                });
        }, 5000);
    };

    useEffect(() => {
        connection?.off("Game");
        connection?.on("Game", (game, start) => {
            console.log("RECEIVED GAME", game, start);
            game.rack.forEach((t, i) => (t.id = t.tile.letter + i));
            setGame(game);
            setPendingPlayOut(game.pendingPlayOut ?? false);
            const finished = game.status === "finished";
            setIsFinished(finished);
            if (finished) {
                setShowGameOverModal(true);
                // Derive scores from scoreBoard for the reconnect/page-reload case
                // (GameFinished event won't fire again after reconnect).
                if (game.players && game.scoreBoard?.rounds) {
                    const scores = game.players.map((p, i) => {
                        const cumulative = [...game.scoreBoard.rounds].reverse()
                            .find(r => (r.turns?.length ?? 0) > i)?.turns[i]?.culmulativeScore ?? 0;
                        return { playerName: p.name, score: cumulative };
                    });
                    const maxScore = scores.length > 0 ? Math.max(...scores.map(s => s.score)) : 0;
                    setFinalScores(scores);
                    setWinners(scores.filter(s => s.score === maxScore).map(s => s.playerName));
                }
            }
        });
        getGame(gameId);

        connection?.on("TurnPlayed", () => {
            console.log("TURN PLAYED");
            getGame(gameId);
        })

        connection?.off("InvalidWord");
        connection?.on("InvalidWord", () => {
            console.warn("INVALID WORD");
            setShowInvalidWord(true);
        });

        connection?.off("OutOfTurn");
        connection?.on("OutOfTurn", () => {
            console.warn("OUT OF TURN");
            setShowOutOfTurn(true);
            getGame(gameId);
        });

        connection?.off("BackendError");
        connection?.on("BackendError", (message) => {
            console.error("BACKEND ERROR", message);
            if (showBackendErrorsRef.current) {
                setBackendError(message);
            }
        });

        connection?.off("Bag");
        connection?.on("Bag", (tiles) => {
            setBagTiles(tiles);
        });

        connection?.off("ChallengeResult");
        connection?.on("ChallengeResult", (result) => {
            console.log("CHALLENGE RESULT", result);
            setChallengeResult(result);
        });

        // T022: auto-open challenge modal for play-out scenario — only for the current player
        connection?.off("PlayOutChallenge");
        connection?.on("PlayOutChallenge", (payload) => {
            console.log("PLAY OUT CHALLENGE", payload);
            setPendingPlayOut(true);
            setPlayOutChallengeWords(payload.wordsPlayed ?? []);
            setPlayOutPlayerName(payload.playedOutPlayerName ?? "");
            // Only auto-open the challenge modal for the player whose turn it now is.
            if (gameMetaRef.current.playerIndex === gameMetaRef.current.currentPlayer) {
                setShowChallengeModal(true);
            }
        });

        // T023: show game over modal when game is finished
        connection?.off("GameFinished");
        connection?.on("GameFinished", (payload) => {
            console.log("GAME FINISHED", payload);
            setIsFinished(true);
            setFinishReason(payload.finishReason ?? null);
            setFinalScores(payload.finalScores ?? []);
            setWinners(payload.winners ?? []);
            setShowGameOverModal(true);
        });

        // Only fetch from server when we don't already have this game loaded.
        // On return navigation (navigate away then back), App.js preserves the game
        // state and the mutated rack positions are still correct. Calling getGame here
        // would race with the unmount SaveRack — if GetGame resolves first, the server
        // sends back stale rack positions (before SaveRack is applied) and the rack reverts.
        if (!game?.id || game.id !== parseInt(gameId, 10)) {
            getGame(gameId);
        }

        window.removeEventListener("resize", (event) => {
            console.log("GAME RESIZE", event);
            calcBoardConfig();
            calcRackScale();
        });
        window.addEventListener("resize", (event) => {
            console.log("GAME RESIZE", event);
            calcBoardConfig();
            calcRackScale();
        });
        window.removeEventListener("mousemove", window.onmousemove);
        window.addEventListener("mousemove", moveMouse);
        return () => {
            window.removeEventListener("resize", window.onresize);
            window.removeEventListener("mousemove", moveMouse);
        };
    }, []);

    const getBoardBoundingBox = () => getBoardBoundingBoxFn(boardScale);

    const moveMouse = (event) => {
        const boardBounding = getBoardBoundingBox();
        if (!boardBounding) return;

        setDebug(boardBounding);

        setClientX(event.clientX);
        setClientY(event.clientY);
        if (boardBounding.left <= event.clientX && event.clientX <= boardBounding.right &&
            boardBounding.top <= event.clientY && event.clientY <= boardBounding.bottom) {
            setOverBoard(true);
        } else {
            setOverBoard(false);
        }
    }

    // TODO: Implement play() — intended to preview/validate the current word
    // before committing via playWord(). Currently unused; wired to the Actions bar.
    const play = () => {
        console.log("PLAY", turn?.words[0])
    }

    const countPlayedTiles = () => setPlayedTileCount(countPlayedTilesFn(game?.board));

    const getScore = () => getScoreFn({ game, connection });

    // US4: wrap playWord to guard against disconnected state.
    const playWord = () => {
        if (connectionStatus !== "connected") {
            setShowConnectionLost(true);
            return;
        }
        playWordFn({ game, connection });
    };

    const handleOpenExchange = () => {
        clearTilesFromBoard();
        setShowExchangeModal(true);
    };

    const handleExchange = (tilesToExchange) => {
        setShowExchangeModal(false);
        if (connectionStatus !== "connected") {
            setShowConnectionLost(true);
            return;
        }
        if (connection && connection.state === "Connected") {
            connection.invoke("Exchange", parseInt(game.id), parseInt(game.playerIndex), tilesToExchange);
        }
    };

    const handleCancelExchange = () => {
        setShowExchangeModal(false);
    };

    const handlePass = () => {
        clearTilesFromBoard();
        if (connectionStatus !== "connected") {
            setShowConnectionLost(true);
            return;
        }
        if (connection && connection.state === "Connected") {
            connection.invoke("Pass", parseInt(game.id), parseInt(game.playerIndex));
        }
    };

    // --- Challenge handlers ---
    // Derive the previous turn from the scoreboard so we can show the words in the modal.
    const getPreviousTurn = (g) => {
        if (!g) return null;
        const playerCount = g.players?.length ?? 0;
        if (playerCount === 0) return null;
        const prevPlayerIndex = (g.currentPlayer - 1 + playerCount) % playerCount;
        const prevRound = g.currentPlayer > 0 ? g.currentRound : g.currentRound - 1;
        return g.scoreBoard?.rounds?.[prevRound]?.turns?.[prevPlayerIndex] ?? null;
    };

    const isMyTurn = game?.playerIndex === game?.currentPlayer;
    const playerCount = game?.players?.length ?? 0;
    const previousTurn = game ? getPreviousTurn(game) : null;
    // canChallenge deliberately does NOT check bag count or previous player's rack count:
    // the final-play scenario (bag empty, previous player's rack now empty) still has
    // previousTurn.type === "word", so the Challenge button remains available as required.
    const canChallenge = isMyTurn && playerCount > 1 && previousTurn?.type === "word";
    // T027: disable action buttons when game is finished or play-out challenge is pending.
    // Challenge is NOT blocked during pendingPlayOut — the current player must still decide.
    const actionsIsMyTurn = isMyTurn && !isFinished && !pendingPlayOut;
    const canChallengeNow = canChallenge && !isFinished;
    const challengedPlayerIndex = game ? (game.currentPlayer - 1 + playerCount + playerCount) % playerCount : 0;
    const challengedPlayerName = game?.players?.[challengedPlayerIndex]?.name ?? "";
    const challengeWords = previousTurn?.wordsPlayed ?? [];

    const handleOpenChallenge = () => {
        clearTilesFromBoard();
        setShowChallengeModal(true);
    };

    const handleCancelChallenge = () => {
        setShowChallengeModal(false);
    };

    const handleChallenge = () => {
        setShowChallengeModal(false);
        if (connectionStatus !== "connected") {
            setShowConnectionLost(true);
            return;
        }
        if (connection && connection.state === "Connected") {
            connection.invoke("Challenge", parseInt(game.id), parseInt(game.playerIndex));
        }
    };

    const handleDismissGameOver = () => setShowGameOverModal(false);

    // T025: acknowledge play-out without challenging
    const handleAcknowledgePlayOut = () => {
        setShowChallengeModal(false);
        setPendingPlayOut(false);
        if (connectionStatus !== "connected") {
            setShowConnectionLost(true);
            return;
        }
        if (connection && connection.state === "Connected") {
            connection.invoke("AcknowledgePlayOut", parseInt(game.id), parseInt(game.playerIndex));
        }
    };

    const handleDismissResult = () => {
        setChallengeResult(null);
    };

    useEffect(() => {
        if (!game) return;
        if (!sized) {
            calcBoardConfig();
            calcRackScale();
            setSized(true);
        }
        rackTilesRef.current = game.rack;
        rackDirtyRef.current = false;
        clearTimeout(saveTimerRef.current);
        gameMetaRef.current = { id: game.id, playerIndex: game.playerIndex, currentPlayer: game.currentPlayer };
        setRackTiles(game.rack);
        if (devMode) {
            connection?.invoke("GetBag", game.id).catch(() => {});
        }
        setBoardBoundingBox(document
            .querySelector(".board")
            ?.getBoundingClientRect());

        setRackBoundingBox(document
            .querySelector(".rack")
            ?.getBoundingClientRect());
        
    }, [game]);

    // Debounce timer is managed by setRackTilesUser above — no [rackTiles] effect needed.

    // Unmount save (US2): if rack is dirty when navigating away, save immediately
    useEffect(() => {
        return () => {
            clearTimeout(saveTimerRef.current);
            if (rackDirtyRef.current) {
                rackDirtyRef.current = false;
                connectionRef.current?.invoke(
                    "SaveRack",
                    gameMetaRef.current.id,
                    gameMetaRef.current.playerIndex,
                    rackTilesRef.current
                ).catch(() => {});
            }
            // Clear game state on unmount to prevent flash of previous game
            setGame(null);
        };
    }, []);

    const calcBoardConfig = () =>
        calcBoardConfigFn({
            setSidebarWidth,
            setTooShort,
            setTab,
            setBoardConfig,
            setBoardScale,
            setRackWidth,
            setRackScale,
        });

    //TODO: Is this one necessary?
    const calcRackScale = (boardScale) =>
        calcRackScaleFn(boardScale, { setRackWidth, setRackScale });

    const mouseDown = (event, tile) =>
        mouseDownFn(event, tile, { setGrabbedTile, setLastClientX, setMouseDownClientX });

    const mouseClickOnRack = (event, clickedTile) =>
        mouseClickOnRackFn(event, clickedTile, {
            mouseDownClientX,
            rackTiles,
            setRackTiles: setRackTilesUser,
            setPickedUpTile,
            setFloatingTile,
            setGrabbedTile,
        });

    const mouseUp = (event) => mouseUpFn(event, { setGrabbedTile, setTileMoved });

    const moving = (event) =>
        movingFn(event, {
            grabbedTile,
            rackTiles,
            lastClientX,
            rackScale,
            setTileMoved,
            setGrabbedTile,
            setLastClientX,
            setRackTiles: setRackTilesUser,
        });
    
    const addTileToRack = (tile, x) =>
        addTileToRackFn(tile, x, { rackBoundingBox, rackTiles, game, setRackTiles: setRackTilesUser, setGame });

    const addFloatingTile = (tile) => addFloatingTileFn(tile, { setFloatingTile });

    const removeFloatingTile = () => removeFloatingTileFn({ setFloatingTile });


    const floatingTileClickHandler = (event) =>
        floatingTileClickHandlerFn(event, {
            boardBoundingBox,
            boardScale,
            floatingTile,
            rackBoundingBox,
            rackScale,
            rackWidth,
            game,
            rackTiles,
            setSpaceWithBlank,
            setShowBlankSelector,
            setGame,
            setFloatingTile,
            getScore,
            addTileToRack,
            countPlayedTiles,
            isMyTurn,
        });

    const clickBoardTile = (space) =>
        clickBoardTileFn(space, {
            rackTiles,
            game,
            setRackTiles,
            setGame,
            setFloatingTile,
            getScore,
            setPlayedTileCount,
            isMyTurn,
        });

    const chooseForBlank = (letter) =>
        chooseForBlankFn(letter, { spaceWithBlank, game, rackTiles, setGame, setShowBlankSelector });

    const clearTilesFromBoard = () =>
        clearTilesFromBoardFn({
            game,
            rackTiles,
            setRackTiles,
            setGame,
            setPlayedTileCount,
        });

    // Guard: if game is null (cleared on unmount), render nothing.
    // Must be after all hooks to avoid "Rendered fewer hooks" error.
    if (!game) return null;

    return (
        <>
            <div style={{display: "flex"}}>
                <div className="play" style={{display: boardConfig == "hd" ? "block": "none", width: sidebarWidth}}>
                    <Summary clearTilesFromBoard={clearTilesFromBoard} boardConfig={boardConfig} game={game} tilesOnBoard={playedTileCount} turn={turn} play={playWord} isMyTurn={actionsIsMyTurn} onExchange={handleOpenExchange} onPass={handlePass} onChallenge={handleOpenChallenge} canChallenge={canChallengeNow}></Summary>
                    <Chat></Chat>
                </div>
                <div id="boardAndRack" style={{display: "flex", flexDirection: "column", margin: "0 auto", width: boardScale * 16, position: "relative"}}>
                    <div className="topBar" style={{backgroundColor: "yellow", height: `calc(100vh - 56px - ${rackScale}px)`, width: "100vw", display: sidebarWidth === 0 ? "block": "none"}}>

                    </div>
                    {boardConfig === "rackonly" ? <></> :
                    <Board clickBoardTile={clickBoardTile} board={game.board} scale={boardScale} setOverBoard={setOverBoard}></Board>
                    }
                    <Rack
                        setDebug={setDebug}
                        moving={moving}
                        mouseDown={mouseDown}
                        mouseUp={mouseUp}
                        mouseClick={mouseClickOnRack}
                        addFloatingTile={addFloatingTile}
                        removeFloatingTile={removeFloatingTile}
                        scale={rackScale}
                        rackWidth={rackWidth}
                        boardScale={boardScale}
                        boardWidth={boardScale * 16}
                        tiles={rackTiles || []}
                    ></Rack>
                    {devMode && (
                        <div style={{fontFamily: "monospace", fontSize: "0.85em", padding: "2px 4px", opacity: 0.7}}>
                            Bag: {[...bagTiles].sort((a, b) => (a.letter || "_").localeCompare(b.letter || "_")).map(t => t.letter || "_").join("")}
                        </div>
                    )}
                    {showBlankSelector ? 
                <div className="blankSelector" style={{top: `calc((${boardScale * 16}px - 16em) / 2)`, left: `calc((${boardScale * 16}px - 28em)/2)`}}>
                    <div className="blankSelectRow">
                        {"ABCDEF".split("").map(letter => (
                            <div className="blankSelectLetter" key={letter} onClick={() => chooseForBlank(letter)}>{letter}</div>
                        ))}
                    </div>
                    <div className="blankSelectRow">
                    {"GHIJKLM".split("").map(letter => (
                            <div className="blankSelectLetter" key={letter} onClick={() => chooseForBlank(letter)}>{letter}</div>
                        ))}
                    </div>
                    <div className="blankSelectRow">
                    {"NOPQRST".split("").map(letter => (
                            <div className="blankSelectLetter" key={letter} onClick={() => chooseForBlank(letter)}>{letter}</div>
                        ))}
                    </div>
                    <div className="blankSelectRow">
                    {"UVWXYZ".split("").map(letter => (
                            <div className="blankSelectLetter" key={letter} onClick={() => chooseForBlank(letter)}>{letter}</div>
                        ))}
                    </div>
                </div>
                :<></>
                }
                </div>
                <div className="sidebar" style={{width: sidebarWidth, display: sidebarWidth === 0 ? "none" : "block"}}>
                    <div className="tabs" style={{display: boardConfig !== "hd" ? "flex" : "none"}}>
                        <div className="tab" style={{width: "10px"}}></div>
                        <div 
                            className={`tab ${tab == "game" ? "active" : ""}`}
                            onClick={() => setTab("game")}
                        >Game</div>
                        <div 
                            onClick={() => setTab("scoreboard")}
                            className={`tab ${tab == "scoreboard" ? "active" : ""}`}
                        >Scoreboard</div>
                        <div className="tab" style={{width: "100%"}}></div>
                    </div>
                    <div className="play" style={{display: boardConfig !== "hd" && tab == "game" ? "block": "none", width: sidebarWidth}}>
                        <Summary clearTilesFromBoard={clearTilesFromBoard} boardConfig={boardConfig} game={game} tilesOnBoard={playedTileCount} turn={turn} play={playWord} isMyTurn={actionsIsMyTurn} onExchange={handleOpenExchange} onPass={handlePass} onChallenge={handleOpenChallenge} canChallenge={canChallengeNow}></Summary>
                        <Chat></Chat>
                    </div>
                    <ScoreBoard style={{display: (boardConfig === "hd" || tab === "scoreboard") ? "block" : "none"}}
                            info = {{boardConfig, tab}}
                            players={game.players}
                            scoreBoard={game.scoreBoard}
                        ></ScoreBoard>
                        {/* {import.meta.env.DEV && (
                            <>
                                <div>MOUSE X: {clientX}, MOUSE Y: {clientY}</div>
                                <div>OVERBOARD: {overBoard.toString()}</div>
                                <div>BOARD BOUNDING:</div>
                                <pre>{JSON.stringify(boardBoundingBox, null, 2)}</pre>
                                <div>DEBUG:</div>
                                <pre>{JSON.stringify(debug, null, 2)}</pre>
                            </>
                        )} */}
                </div>

                <FloatingTile
                    floatingTile={floatingTile}
                    clientX={clientX}
                    clientY={clientY}
                    boardScale={boardScale}
                    rackScale={rackScale}
                    overBoard={overBoard}
                    floatingTileClickHandler={floatingTileClickHandler}
                />
                
               
            </div>

            {/* Game Over Modal */}
            <Modal show={showGameOverModal} onHide={handleDismissGameOver} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Game Over</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {finishReason && (
                        <p className="text-muted mb-2">
                            {finishReason === "play-out" ? "A player played out" : finishReason}
                        </p>
                    )}
                    {winners.length > 0 && (
                        <p className="fs-5 fw-bold">
                            {winners.length === 1
                                ? `${winners[0]} wins with ${finalScores.find(fs => fs.playerName === winners[0])?.score ?? 0} pts!`
                                : `${winners.join(" and ")} tie with ${finalScores.find(fs => fs.playerName === winners[0])?.score ?? 0} pts!`}
                        </p>
                    )}
                    {finalScores.length > 0 && (
                        <ul>
                            {finalScores.map((fs, i) => (
                                <li key={i}>{fs.playerName}: {fs.score} pts</li>
                            ))}
                        </ul>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleDismissGameOver}>Close</Button>
                    <Link to="/" className="btn btn-primary">Back to Games</Link>
                </Modal.Footer>
            </Modal>

            {/* Exchange Modal */}
            <ExchangeModal
                show={showExchangeModal}
                tiles={rackTiles}
                boardScale={boardScale}
                bagCount={game.bagCount}
                onExchange={handleExchange}
                onCancel={handleCancelExchange}
            />

            {/* Challenge Modal */}
            <ChallengeModal
                show={showChallengeModal}
                words={pendingPlayOut ? playOutChallengeWords : challengeWords}
                challengedPlayerName={pendingPlayOut ? playOutPlayerName : challengedPlayerName}
                onChallenge={handleChallenge}
                onCancel={handleCancelChallenge}
                pendingPlayOut={pendingPlayOut}
                onAcknowledge={handleAcknowledgePlayOut}
            />

            {/* Challenge Result Modal */}
            <Modal show={challengeResult !== null} onHide={handleDismissResult} centered>
                <Modal.Header closeButton>
                    <Modal.Title className={challengeResult?.outcome === "Successful" ? "text-success" : "text-danger"}>
                        Challenge {challengeResult?.outcome}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {challengeResult?.outcome === "Successful"
                            ? `${challengeResult?.challengerPlayerName} successfully challenged ${challengeResult?.challengedPlayerName}'s play.`
                            : `${challengeResult?.challengerPlayerName}'s challenge failed — the words were all legal.`}
                    </p>
                    <ul>
                        {challengeResult?.words?.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleDismissResult}>OK</Button>
                </Modal.Footer>
            </Modal>

            {/* Invalid Word Modal */}
            <Modal show={showInvalidWord} onHide={() => setShowInvalidWord(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Invalid Play</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    The word or tile placement could not be validated. Please check your tile positions and try again.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowInvalidWord(false)}>OK</Button>
                </Modal.Footer>
            </Modal>

            {/* Out of Turn Modal */}
            <Modal show={showOutOfTurn} onHide={() => setShowOutOfTurn(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Not Your Turn</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    It is not your turn. Please wait for the current player to finish their move.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowOutOfTurn(false)}>OK</Button>
                </Modal.Footer>
            </Modal>

            {/* Backend Error Modal */}
            <Modal show={backendError !== null} onHide={() => setBackendError(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Server Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>An error occurred on the server:</p>
                    <pre style={{color: "red", fontSize: "0.85em", whiteSpace: "pre-wrap"}}>{backendError}</pre>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={() => setBackendError(null)}>Dismiss</Button>
                </Modal.Footer>
            </Modal>

            {/* US4: Connection Lost Modal — shown when a game action is attempted while disconnected */}
            <Modal show={showConnectionLost} onHide={() => setShowConnectionLost(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Connection Lost</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Your action was not sent because the connection is lost. Please wait to reconnect or refresh the page, then try again.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowConnectionLost(false)}>OK</Button>
                </Modal.Footer>
            </Modal>

            {/* Backend Error Toggle — fixed bottom-right */}
            <div
                onClick={toggleBackendErrors}
                title={showBackendErrors ? "Server error reporting ON. Click to disable." : "Server error reporting OFF. Click to enable."}
                style={{position: "fixed", bottom: "8px", right: "8px", zIndex: 1000,
                    fontSize: "0.7em", cursor: "pointer", opacity: 0.4,
                    background: "#000", color: "#fff", padding: "2px 6px", borderRadius: "4px"}}
            >
                🛠 {showBackendErrors ? "✓" : "✗"}
            </div>

        </>
    );
}

export default Game;
